import React from 'react';
import { Card } from 'components/ui/card';
import { cn, formatIndianNumber } from 'lib/utils';
import { AlertTriangle } from 'lucide-react';
import { LhcStats } from '../api';

interface LhcStatCardsProps {
    stats?: LhcStats | null;
    onStatCardClick?: (label: 'Min' | 'Max' | 'Avg' | 'Outliers' | 'Total', value: number) => void;
    activeStat?: 'Min' | 'Max' | 'Avg' | 'Outliers' | null;
}

const LhcStatCards: React.FC<LhcStatCardsProps> = ({ stats, onStatCardClick, activeStat }) => {
    if (!stats) return null;

    const outlierCount = stats.outlier_count ?? 0;
    const avgCount = stats.avg_count ?? 0;
    const avgActive = activeStat === 'Avg';
    const outliersActive = activeStat === 'Outliers';
    const totalActive = activeStat === null; // "showing all" is the Total state

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {/* Total Count — click to show ALL LHCs (clears any active filter) */}
            <Card
                variant="neutral"
                onClick={() => onStatCardClick?.('Total', stats.total_count ?? 0)}
                className={cn(
                    'p-3.5 select-none',
                    onStatCardClick && 'cursor-pointer transition-transform hover:scale-[1.01] active:scale-[0.99]',
                    totalActive && 'ring-2 ring-offset-2 ring-slate-400'
                )}
            >
                <div className="text-xs font-medium text-slate-600">Total Count</div>
                <div className="mt-1 text-xl font-semibold tabular-nums text-slate-800">
                    {stats.total_count ?? '-'}
                </div>
            </Card>

            {/* Avg Rate — the quote rate (HERO) */}
            <Card
                variant="total"
                onClick={() => onStatCardClick?.('Avg', stats.avg_value)}
                className={cn(
                    'p-3.5 select-none ring-1 ring-blue-300',
                    onStatCardClick && 'cursor-pointer transition-transform hover:scale-[1.01] active:scale-[0.99]',
                    avgActive && 'ring-2 ring-offset-2 ring-blue-500'
                )}
            >
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-blue-700">Avg Rate</span>
                        <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">Quote</span>
                    </div>
                    <span className="text-xs text-slate-500 tabular-nums">
                        {avgCount} LHC{avgCount === 1 ? '' : 's'}
                    </span>
                </div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                    ₹{formatIndianNumber(stats.avg_value)}
                </div>
            </Card>

            {/* Min / Max (interactive filter cards) */}
            {([
                { label: 'Min', count: stats.min_count, value: stats.min_value, variant: 'violet', ring: 'ring-violet-400' },
                { label: 'Max', count: stats.max_count, value: stats.max_value, variant: 'violet', ring: 'ring-violet-400' },
            ] as const).map((item) => {
                const isActive = activeStat === item.label;
                const count = item.count ?? 0;
                return (
                    <Card
                        key={item.label}
                        variant={item.variant}
                        onClick={() => onStatCardClick?.(item.label, item.value)}
                        className={cn(
                            'p-3.5 select-none',
                            onStatCardClick && 'cursor-pointer transition-transform hover:scale-[1.01] active:scale-[0.99]',
                            isActive && cn('ring-2 ring-offset-2', item.ring)
                        )}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium text-slate-600">{item.label} Value</span>
                            <span className="text-xs text-slate-500 tabular-nums">
                                {count} LHC{count === 1 ? '' : 's'}
                            </span>
                        </div>
                        <div className="mt-1 text-xl font-semibold tabular-nums text-slate-800">
                            ₹{formatIndianNumber(item.value)}
                        </div>
                    </Card>
                );
            })}

            {/* Outliers — LHCs beyond ±10% of the median */}
            <Card
                variant="negative"
                onClick={outlierCount > 0 ? () => onStatCardClick?.('Outliers', outlierCount) : undefined}
                className={cn(
                    'p-3.5 select-none',
                    outlierCount > 0 && onStatCardClick
                        ? 'cursor-pointer transition-transform hover:scale-[1.01] active:scale-[0.99]'
                        : 'opacity-60',
                    outliersActive && 'ring-2 ring-offset-2 ring-red-500'
                )}
            >
                <div className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                    <span className="text-xs font-medium text-red-700">Outliers</span>
                </div>
                <div className="mt-1 text-xl font-semibold tabular-nums text-red-700">
                    {outlierCount}
                </div>
            </Card>
        </div>
    );
};

export default LhcStatCards;
